import React, { createContext, useContext, useReducer, useCallback, ReactNode, useEffect } from 'react';
import {
  collection, addDoc, updateDoc, deleteDoc, doc, getDocs, query, where,
  Timestamp, FirestoreDataConverter, QueryDocumentSnapshot, SnapshotOptions,
  getDoc, setDoc
} from 'firebase/firestore';
import { onAuthStateChanged } from 'firebase/auth';
import { auth, db } from '@/lib/firebase';
import { useBudgetManager } from './BudgetManagerContext';
import { useAccount } from './AccountContext';

// Define types
export type BudgetItem = {
  id?: string;
  name: string;
  amount: number;
  targetAmount: number;
  spent: number;
  category: string;
  date: Date;
  createdBy?: string;
  symbol?: string;
};

type BudgetData = {
  items: Record<string, BudgetItem[]>;
};

type BudgetState = {
  budgetItems: BudgetItem[];
  totalBudget: number;
  assignedBudget: number;
  availableToBudget: number;
  categories: string[];
  month: number;
  year: number;
  loading: boolean;
  error: string | null;
};

type BudgetAction =
  | { type: 'LOAD_BUDGET_ITEMS'; payload: Partial<BudgetState> }
  | { type: 'ADD_BUDGET_ITEM'; payload: BudgetItem }
  | { type: 'UPDATE_BUDGET_ITEM'; payload: BudgetItem }
  | { type: 'DELETE_BUDGET_ITEM'; payload: string }
  | { type: 'CHANGE_MONTH'; payload: { month: number; year: number } }
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'SET_ERROR'; payload: string | null }
  | { type: 'UPDATE_AVAILABLE_BUDGET'; payload: number };

type BudgetContextType = {
  state: BudgetState;
  loadBudgetItems: (filters?: { category?: string; month?: number; year?: number }) => Promise<void>;
  addBudgetItem: (budgetItem: Omit<BudgetItem, 'id' | 'createdBy'>) => Promise<void>;
  updateBudgetItem: (budgetItem: BudgetItem) => Promise<void>;
  deleteBudgetItem: (budgetItemId: string) => Promise<void>;
  changeMonth: (month: number, year: number) => void;
  autoAssign: () => Promise<void>;
  assignMoney: (categoryId: string, amount: number) => Promise<void>;
  clearError: () => void;
};

const BudgetContext = createContext<BudgetContextType | undefined>(undefined);

const initialState: BudgetState = {
  budgetItems: [],
  totalBudget: 0,
  assignedBudget: 0,
  availableToBudget: 0,
  categories: [],
  month: new Date().getMonth() + 1,
  year: new Date().getFullYear(),
  loading: false,
  error: null,
};

function budgetReducer(state: BudgetState, action: BudgetAction): BudgetState {
  switch (action.type) {
    case 'LOAD_BUDGET_ITEMS':
      return { ...state, ...action.payload, loading: false };
    case 'ADD_BUDGET_ITEM':
      return { ...state, budgetItems: [...state.budgetItems, action.payload], loading: false };
    case 'UPDATE_BUDGET_ITEM':
      return {
        ...state,
        budgetItems: state.budgetItems.map((item) =>
          item.id === action.payload.id ? action.payload : item
        ),
        loading: false,
      };
    case 'DELETE_BUDGET_ITEM':
      return {
        ...state,
        budgetItems: state.budgetItems.filter((item) => item.id !== action.payload),
        loading: false,
      };
    case 'CHANGE_MONTH':
      return { ...state, month: action.payload.month, year: action.payload.year, loading: true };
    case 'SET_LOADING':
      return { ...state, loading: action.payload };
    case 'SET_ERROR':
      return { ...state, error: action.payload, loading: false };
    case 'UPDATE_AVAILABLE_BUDGET':
      return { ...state, availableToBudget: action.payload };
    default:
      return state;
  }
}

const budgetItemConverter: FirestoreDataConverter<BudgetItem> = {
  toFirestore: (budgetItem: BudgetItem) => {
    return {
      ...budgetItem,
      date: Timestamp.fromDate(budgetItem.date),
    };
  },
  fromFirestore: (
    snapshot: QueryDocumentSnapshot,
    options: SnapshotOptions
  ): BudgetItem => {
    const data = snapshot.data(options);
    return {
      ...data,
      id: snapshot.id,
      date: data.date instanceof Timestamp ? data.date.toDate() : new Date(data.date),
    } as BudgetItem;
  },
};

// Define default categories and items
const defaultCategories = {
  'wants': ['entertainment', 'shopping', 'travel', 'gifts'],
  'needs': ['foodDining', 'groceries', 'transportation', 'health', 'education'],
  'bills': ['utilities', 'rental', 'phone', 'internet'],
  'income': ['sales']
};

const categorySymbols: { [key: string]: string } = {
  'foodDining': '🍽️',
  'transportation': '🚗',
  'utilities': '🔌',
  'entertainment': '🎬',
  'shopping': '🛒',
  'health': '⚕️',
  'travel': '✈️',
  'education': '🎓',
  'gifts': '🎁',
  'phone': '📱',
  'internet': '🛜',
  'groceries': '🛒',
  'rental': '🏠',
  'sales': '🧾'
};

export function BudgetProvider({ children }: { children: ReactNode }) {
  const [state, dispatch] = useReducer(budgetReducer, initialState);
  const { state: budgetManagerState } = useBudgetManager();
  const { state: accountState } = useAccount();

  const checkUserAccess = async (userId: string, currentBudget: any) => {
    if (!currentBudget.isShared) {
      return true;
    }

    const budgetDocRef = doc(db, 'shared_budgets', currentBudget.id);
    const budgetDocSnap = await getDoc(budgetDocRef);

    if (!budgetDocSnap.exists()) {
      throw new Error('Shared budget not found');
    }

    const data = budgetDocSnap.data();
    const sharedWith = data?.sharedWith || [];
    return sharedWith.includes(userId) || data?.ownerId === userId;
  };

  const createDefaultBudgetItems = async (userId: string, currentBudget: any) => {
    const budgetItems: Record<string, BudgetItem[]> = {};

    for (const [parentCategory, subCategories] of Object.entries(defaultCategories)) {
      budgetItems[parentCategory] = subCategories.map(subCategory => ({
        name: subCategory,
        amount: 0,
        targetAmount: 0,
        spent: 0,
        category: parentCategory,
        date: new Date(),
        createdBy: userId,
        symbol: categorySymbols[subCategory] || '📌'
      }));
    }

    const budgetRef = doc(db, 'budgets', currentBudget.id);
    await setDoc(budgetRef, { items: budgetItems }, { merge: true });
  };

  // Add this right after the budgetItemConverter definition
  const processBudgetData = (budgetData: BudgetData, month: number, year: number) => {
    if (!budgetData || !budgetData.items) {
      throw new Error('Invalid budget data');
    }

    // Filter items by date range
    const budgetItems = Object.entries(budgetData.items).flatMap(([category, items]) =>
      items.map((item: BudgetItem) => ({
        ...item,
        id: `${category}_${item.name}`,
        category
      }))
    );

    const totalBudget = budgetItems.reduce((sum, item) => sum + item.targetAmount, 0);
    const assignedBudget = budgetItems.reduce((sum, item) => sum + item.amount, 0);
    const categories = Array.from(new Set(budgetItems.map(item => item.category)));

    return {
      budgetItems,
      totalBudget,
      assignedBudget,
      categories,
      month,
      year,
    };
  };

  const createMonthlyBudgetItems = useCallback(async (
    userId: string,
    currentBudget: any,
    targetMonth: number,
    targetYear: number
  ) => {
    // Create a unique ID for the monthly budget
    const monthlyBudgetId = `${currentBudget.id}_${targetYear}_${targetMonth}`;
    const budgetRef = doc(db, 'budgets', monthlyBudgetId);
    const existingBudgetDoc = await getDoc(budgetRef);

    let budgetItems: Record<string, BudgetItem[]> = {};

    // First check if there's an existing budget for this month
    if (existingBudgetDoc.exists()) {
      return existingBudgetDoc.data().items;
    }

    // Check for previous month's budget to use as template
    const prevMonth = targetMonth === 1 ? 12 : targetMonth - 1;
    const prevYear = targetMonth === 1 ? targetYear - 1 : targetYear;
    const prevBudgetId = `${currentBudget.id}_${prevYear}_${prevMonth}`;
    const prevBudgetRef = doc(db, 'budgets', prevBudgetId);
    const prevBudgetDoc = await getDoc(prevBudgetRef);

    if (prevBudgetDoc.exists()) {
      // Copy from previous month's budget
      const prevData = prevBudgetDoc.data();
      for (const [category, items] of Object.entries(prevData.items)) {
        budgetItems[category] = (items as BudgetItem[]).map(item => ({
          name: item.name,
          amount: 0,
          targetAmount: item.targetAmount,
          spent: 0,
          category: item.category,
          date: new Date(targetYear, targetMonth - 1),
          createdBy: userId,
          symbol: item.symbol || '📌'
        }));
      }
    } else {
      // Create from default categories
      for (const [parentCategory, subCategories] of Object.entries(defaultCategories)) {
        budgetItems[parentCategory] = subCategories.map(subCategory => ({
          name: subCategory,
          amount: 0,
          targetAmount: 0,
          spent: 0,
          category: parentCategory,
          date: new Date(targetYear, targetMonth - 1),
          createdBy: userId,
          symbol: categorySymbols[subCategory] || '📌'
        }));
      }
    }

    // Create the monthly budget document
    await setDoc(budgetRef, {
      items: budgetItems,
      month: targetMonth,
      year: targetYear,
      budgetId: currentBudget.id, // Link to parent budget
      createdBy: userId,
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now()
    });

    return budgetItems;
  }, []);

  const loadBudgetItems = useCallback(async (filters?: { category?: string; month?: number; year?: number }) => {
    dispatch({ type: 'SET_LOADING', payload: true });
    try {
      const user = auth.currentUser;
      if (!user) throw new Error('User not authenticated');

      const currentBudget = budgetManagerState.currentBudget;
      if (!currentBudget || !currentBudget.id) {
        console.warn('No budget selected or budget has no ID');
        dispatch({ type: 'LOAD_BUDGET_ITEMS', payload: { budgetItems: [] } });
        return;
      }

      const hasAccess = await checkUserAccess(user.uid, currentBudget);
      if (!hasAccess) {
        throw new Error('User does not have access to this budget');
      }

      const { month = state.month, year = state.year } = filters || {};
      const monthlyBudgetId = `${currentBudget.id}_${year}_${month}`;

      // Query the specific monthly budget
      const monthlyBudgetRef = doc(db, 'budgets', monthlyBudgetId);
      const monthlyBudgetDoc = await getDoc(monthlyBudgetRef);

      let budgetData;
      if (!monthlyBudgetDoc.exists()) {
        // Create new monthly budget if it doesn't exist
        console.log('Creating new monthly budget for', month, year);
        const newBudgetItems = await createMonthlyBudgetItems(user.uid, currentBudget, month, year);
        budgetData = { items: newBudgetItems };

        // Proactively create next month's budget
        const nextMonth = month === 12 ? 1 : month + 1;
        const nextYear = month === 12 ? year + 1 : year;
        await createMonthlyBudgetItems(user.uid, currentBudget, nextMonth, nextYear);
      } else {
        budgetData = monthlyBudgetDoc.data();

        // Verify ownership or access
        if (budgetData.createdBy !== user.uid && !hasAccess) {
          throw new Error('Unauthorized access to budget');
        }
      }

      const processedData = processBudgetData({ items: budgetData.items || {} }, month, year);
      dispatch({
        type: 'LOAD_BUDGET_ITEMS',
        payload: processedData
      });

      updateAvailableBudget();
    } catch (error) {
      console.error('Error loading budget items:', error);
      dispatch({ type: 'SET_ERROR', payload: 'Failed to load budget items' });
    }
  }, [state.month, state.year, budgetManagerState.currentBudget, createMonthlyBudgetItems]);

  const addBudgetItem = useCallback(async (budgetItem: Omit<BudgetItem, 'id' | 'createdBy'>) => {
    dispatch({ type: 'SET_LOADING', payload: true });
    try {
      const user = auth.currentUser;
      if (!user) throw new Error('User not authenticated');

      const currentBudget = budgetManagerState.currentBudget;
      if (!currentBudget) throw new Error('No budget selected');

      const monthlyBudgetId = `${currentBudget.id}_${state.year}_${state.month}`;
      const monthlyBudgetRef = doc(db, 'budgets', monthlyBudgetId);

      const newBudgetItem: BudgetItem = {
        ...budgetItem,
        createdBy: user.uid,
        spent: 0,
        date: new Date(state.year, state.month - 1)
      };

      const monthlyBudgetDoc = await getDoc(monthlyBudgetRef);
      if (monthlyBudgetDoc.exists()) {
        const budgetData = monthlyBudgetDoc.data();

        // Verify ownership or access
        if (budgetData.createdBy !== user.uid && !(await checkUserAccess(user.uid, currentBudget))) {
          throw new Error('Unauthorized access to budget');
        }

        const updatedItems = {
          ...budgetData.items,
          [budgetItem.category]: [
            ...(budgetData.items[budgetItem.category] || []),
            newBudgetItem
          ]
        };

        await updateDoc(monthlyBudgetRef, {
          items: updatedItems,
          updatedAt: Timestamp.now()
        });
      } else {
        // Create new monthly budget with this item
        const budgetItems = await createMonthlyBudgetItems(user.uid, currentBudget, state.month, state.year);
        const updatedItems = {
          ...budgetItems,
          [budgetItem.category]: [
            ...(budgetItems[budgetItem.category] || []),
            newBudgetItem
          ]
        };

        await updateDoc(monthlyBudgetRef, {
          items: updatedItems,
          updatedAt: Timestamp.now()
        });
      }

      const addedBudgetItem: BudgetItem = {
        ...newBudgetItem,
        id: `${budgetItem.category}_${budgetItem.name}`,
      };

      dispatch({ type: 'ADD_BUDGET_ITEM', payload: addedBudgetItem });
      updateAvailableBudget();
    } catch (error) {
      console.error('Error adding budget item:', error);
      dispatch({ type: 'SET_ERROR', payload: 'Failed to add budget item' });
    }
  }, [budgetManagerState.currentBudget, state.month, state.year, createMonthlyBudgetItems]);

  const updateBudgetItem = useCallback(async (budgetItem: BudgetItem) => {
    dispatch({ type: 'SET_LOADING', payload: true });
    try {
      const user = auth.currentUser;
      if (!user) throw new Error('User not authenticated');

      const currentBudget = budgetManagerState.currentBudget;
      if (!currentBudget) throw new Error('No budget selected');

      const hasAccess = await checkUserAccess(user.uid, currentBudget);
      if (!hasAccess) {
        throw new Error('User does not have access to this budget');
      }

      if (!budgetItem.id) throw new Error('Budget item ID is required for update');

      // Create the monthly budget ID
      const monthlyBudgetId = `${currentBudget.id}_${state.year}_${state.month}`;
      const monthlyBudgetRef = doc(db, 'budgets', monthlyBudgetId);
      const monthlyBudgetDoc = await getDoc(monthlyBudgetRef);

      if (monthlyBudgetDoc.exists()) {
        const budgetData = monthlyBudgetDoc.data();
        const [category, itemName] = budgetItem.id.split('_');

        // Update the specific item in the monthly budget
        const updatedItems = {
          ...budgetData.items,
          [category]: (budgetData.items[category] || []).map((item: BudgetItem) =>
            item.name === itemName ? { ...item, ...budgetItem } : item
          )
        };

        await updateDoc(monthlyBudgetRef, {
          items: updatedItems,
          updatedAt: Timestamp.now()
        });

        // Reload budget items to reflect changes
        await loadBudgetItems();
      } else {
        throw new Error('Monthly budget not found');
      }
    } catch (error) {
      console.error('Error updating budget item:', error);
      dispatch({ type: 'SET_ERROR', payload: 'Failed to update budget item' });
    }
  }, [budgetManagerState.currentBudget, state.month, state.year, loadBudgetItems]);

  const deleteBudgetItem = useCallback(async (budgetItemId: string) => {
    dispatch({ type: 'SET_LOADING', payload: true });
    try {
      const user = auth.currentUser;
      if (!user) throw new Error('User not authenticated');

      const currentBudget = budgetManagerState.currentBudget;
      if (!currentBudget) throw new Error('No budget selected');

      const hasAccess = await checkUserAccess(user.uid, currentBudget);
      if (!hasAccess) {
        throw new Error('User does not have access to this budget');
      }

      // Create the monthly budget ID
      const monthlyBudgetId = `${currentBudget.id}_${state.year}_${state.month}`;
      const monthlyBudgetRef = doc(db, 'budgets', monthlyBudgetId);
      const monthlyBudgetDoc = await getDoc(monthlyBudgetRef);

      if (monthlyBudgetDoc.exists()) {
        const budgetData = monthlyBudgetDoc.data();
        const [category, itemName] = budgetItemId.split('_');

        if (!budgetData.items[category]) {
          throw new Error('Category not found');
        }

        // Remove the item from the category
        const updatedItems = {
          ...budgetData.items,
          [category]: budgetData.items[category].filter(
            (item: BudgetItem) => item.name !== itemName
          )
        };

        // If the category is empty after removing the item, remove the category as well
        if (updatedItems[category].length === 0) {
          delete updatedItems[category];
        }

        await updateDoc(monthlyBudgetRef, {
          items: updatedItems,
          updatedAt: Timestamp.now()
        });

        dispatch({ type: 'DELETE_BUDGET_ITEM', payload: budgetItemId });

        // Reload budget items to reflect changes
        await loadBudgetItems();

        // Update available budget after deletion
        updateAvailableBudget();
      } else {
        throw new Error('Monthly budget not found');
      }
    } catch (error) {
      console.error('Error deleting budget item:', error);
      dispatch({ type: 'SET_ERROR', payload: 'Failed to delete budget item' });
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  }, [
    budgetManagerState.currentBudget,
    state.month,
    state.year,
    loadBudgetItems,
  ]);

  const changeMonth = useCallback((month: number, year: number) => {
    dispatch({ type: 'CHANGE_MONTH', payload: { month, year } });
    loadBudgetItems({ month, year });
  }, [loadBudgetItems]);

  const updateAvailableBudget = useCallback(() => {
    const availableToBudget = accountState.accounts.reduce((sum, account) => {
      return sum + (account.availableToBudget || 0);
    }, 0);

    const assignedBudget = state.budgetItems.reduce((sum, item) => sum + item.amount, 0);
    const newAvailableBudget = availableToBudget - assignedBudget;

    dispatch({ type: 'UPDATE_AVAILABLE_BUDGET', payload: newAvailableBudget });
  }, [accountState.accounts, state.budgetItems]);

  const autoAssign = useCallback(async () => {
    dispatch({ type: 'SET_LOADING', payload: true });
    try {
      const user = auth.currentUser;
      if (!user) throw new Error('User not authenticated');

      const currentBudget = budgetManagerState.currentBudget;
      if (!currentBudget) throw new Error('No budget selected');

      const hasAccess = await checkUserAccess(user.uid, currentBudget);
      if (!hasAccess) {
        throw new Error('User does not have access to this budget');
      }

      // Create the monthly budget ID
      const monthlyBudgetId = `${currentBudget.id}_${state.year}_${state.month}`;
      const monthlyBudgetRef = doc(db, 'budgets', monthlyBudgetId);
      const monthlyBudgetDoc = await getDoc(monthlyBudgetRef);

      if (!monthlyBudgetDoc.exists()) {
        throw new Error('Monthly budget not found');
      }

      const budgetData = monthlyBudgetDoc.data();
      let remainingBudget = state.availableToBudget;

      // Process items by category
      const updatedItems = { ...budgetData.items };

      for (const [category, items] of Object.entries(budgetData.items)) {
        updatedItems[category] = (items as BudgetItem[]).map((item: BudgetItem) => {
          if (item.amount < item.targetAmount && remainingBudget > 0) {
            const amountToAssign = Math.min(
              item.targetAmount - item.amount,
              remainingBudget
            );
            remainingBudget -= amountToAssign;
            return {
              ...item,
              amount: item.amount + amountToAssign
            };
          }
          return item;
        });
      }

      await updateDoc(monthlyBudgetRef, {
        items: updatedItems,
        updatedAt: Timestamp.now()
      });

      // Reload budget items to reflect changes
      await loadBudgetItems();
      updateAvailableBudget();
    } catch (error) {
      console.error('Error in auto-assign:', error);
      dispatch({ type: 'SET_ERROR', payload: 'Failed to auto-assign budget' });
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  }, [
    state.availableToBudget,
    state.budgetItems,
    state.year,
    state.month,
    budgetManagerState.currentBudget,
    loadBudgetItems,
    updateAvailableBudget
  ]);

  const assignMoney = useCallback(async (itemId: string, amount: number) => {
    dispatch({ type: 'SET_LOADING', payload: true });
    try {
      const user = auth.currentUser;
      if (!user) throw new Error('User not authenticated');

      const currentBudget = budgetManagerState.currentBudget;
      if (!currentBudget) throw new Error('No budget selected');

      const hasAccess = await checkUserAccess(user.uid, currentBudget);
      if (!hasAccess) {
        throw new Error('User does not have access to this budget');
      }

      if (amount > state.availableToBudget) {
        throw new Error('Not enough available budget');
      }

      // Create the monthly budget ID
      const monthlyBudgetId = `${currentBudget.id}_${state.year}_${state.month}`;
      const monthlyBudgetRef = doc(db, 'budgets', monthlyBudgetId);
      const monthlyBudgetDoc = await getDoc(monthlyBudgetRef);

      if (monthlyBudgetDoc.exists()) {
        const budgetData = monthlyBudgetDoc.data() as BudgetData;
        const [category, itemName] = itemId.split('_');

        if (!budgetData.items[category]) {
          throw new Error('Category not found');
        }

        // Update the specific item's amount in the monthly budget
        const updatedItems = {
          ...budgetData.items,
          [category]: budgetData.items[category].map((item: BudgetItem) =>
            item.name === itemName
              ? { ...item, amount: item.amount + amount }
              : item
          )
        };

        await updateDoc(monthlyBudgetRef, {
          items: updatedItems,
          updatedAt: Timestamp.now()
        });

        // Reload budget items to reflect changes
        await loadBudgetItems();
      } else {
        throw new Error('Monthly budget not found');
      }
    } catch (error) {
      console.error('Error in assign money:', error);
      dispatch({ type: 'SET_ERROR', payload: 'Failed to assign money' });
    }
  }, [state.availableToBudget, budgetManagerState.currentBudget, state.month, state.year, loadBudgetItems]);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user) {
        loadBudgetItems();
      } else {
        dispatch({ type: 'LOAD_BUDGET_ITEMS', payload: initialState });
      }
    });

    return () => unsubscribe();
  }, [loadBudgetItems]);

  useEffect(() => {
    updateAvailableBudget();
  }, [updateAvailableBudget, accountState.accounts]);

  const clearError = useCallback(() => {
    dispatch({ type: 'SET_ERROR', payload: null });
  }, []);

  return (
    <BudgetContext.Provider
      value={{
        state,
        loadBudgetItems,
        addBudgetItem,
        updateBudgetItem,
        deleteBudgetItem,
        changeMonth,
        autoAssign,
        assignMoney,
        clearError,
      }}
    >
      {children}
    </BudgetContext.Provider>
  );
}

export function useBudget(): BudgetContextType {
  const context = useContext(BudgetContext);
  if (!context) {
    throw new Error('useBudget must be used within a BudgetProvider');
  }
  return context;
}